/* eslint-disable no-bitwise */
import { Edge, Node } from '@xyflow/react';
import crc32 from 'crc-32';
import pako from 'pako';
import { toast } from 'react-toastify';
import { IVlanItem, GlobalSettingsType } from 'src/context/global/types';
import { globalSettingsDefault } from 'src/modules/network-configuration/dialog/global-settings';
import {
  EDataType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import { IConfigurationContentData } from 'src/types/configuration';

import { configurationDhcp } from './configuration/configuration-dhcp';
import { configurationDhcpClient } from './configuration/configuration-dhcpclient';
import { configurationInterface } from './configuration/configuration-interface';
import { configurationLldp } from './configuration/configuration-lldp';
import { configurationMirroring } from './configuration/configuration-mirroring';
import { configurationPoE } from './configuration/configuration-poe';
import { configurationQos } from './configuration/configuration-qos';
import { configurationSTP } from './configuration/configuration-stp';
import { configurationVlan } from './configuration/configuration-vlan';
import { ensureDefaultUsers } from './default-users';

export const getConfiguration = async ({
  nodes: allNodes,
  edges,
  vlans,
  cfgName,
  targetId,
  isAutoDeploy = false,
  autoDeployIps = [],
  globalSettings,
}: {
  nodes: Node[];
  edges: Edge[];
  vlans: IVlanItem[];
  cfgName: string;
  targetId: string;
  isAutoDeploy: boolean;
  autoDeployIps?: {
    id: string;
    name: string;
    ip: string;
  }[];
  globalSettings: GlobalSettingsType;
}): Promise<File> => {
  if (isAutoDeploy) {
    cfgName = `${cfgName}`;
  }

  const nodes = allNodes.filter(
    (node) =>
      (node.data?.category as NodeCategoryModel)?.type === EDataType.Device ||
      (node.data?.category as NodeCategoryModel)?.type === EDataType.Switch,
  );

  const targetNode = nodes.find((node) => node.id === targetId)?.data;
  if (!targetNode) {
    toast.error('Invalid target node');
    return Promise.reject(new Error('Invalid target node'));
  }
  const hasPoE = (targetNode.category as NodeCategoryModel).ports?.some(
    (port) => port.isPoE,
  );

  const targetNodeCategory = targetNode.category as NodeCategoryModel;
  const allEdges = edges.filter(
    (edge) => edge.source === targetId || edge.target === targetId,
  );

  const ignitionTimeout = parseInt(
    globalSettings.ignitionTimeout?.toString() ?? '1800',
    10,
  );

  const jsonData: IConfigurationContentData = {
    dhcp: configurationDhcp({
      nodeId: targetId,
      nodeCategory: targetNodeCategory,
      edges: allEdges,
      nodes,
      vlans,
      isAutoDeploy,
      autoDeployIps,
    }),
    dhcpclient: configurationDhcpClient({
      nodeCategory: targetNodeCategory,
      isAutoDeploy,
    }),
    ignition: {
      enabled:
        globalSettings.ignitionEnabled ??
        globalSettingsDefault.ignitionEnabled ??
        false,
      timeout: ignitionTimeout,
      version: 1,
    },
    interface: configurationInterface({
      nodeId: targetId,
      nodeCategory: targetNodeCategory,
      edges: allEdges,
      nodes,
      globalSettings,
    }),
    lldp: configurationLldp({
      nodeId: targetId,
      nodeCategory: targetNodeCategory,
    }),
    log: {
      remote: {
        destination: globalSettings?.remoteLogging?.destination || '',
        enabled: globalSettings?.remoteLogging?.enabled || false,
        interval: globalSettings?.remoteLogging?.interval || 60,
        mark: globalSettings?.remoteLogging?.mark || false,
        port: globalSettings?.remoteLogging?.port || 514,
        protocol: globalSettings?.remoteLogging?.protocol || 'udp',
      },
      version: 1,
    },
    mirroring: configurationMirroring({
      nodeId: targetId,
      nodeCategory: targetNodeCategory,
    }),
    multicast: {
      IGMPSnoop: false,
      version: 2,
    },
    nat: {
      enabled: false,
      rules: [],
      version: 2,
    },
    ntp: {
      config: {
        enable: globalSettings?.ntp?.enable || false,
        queryInterval: globalSettings?.ntp?.queryInterval || 12,
        server: globalSettings?.ntp?.server || '',
      },
      version: 2,
    },
    ...(hasPoE
      ? {
          poe: configurationPoE({
            nodeId: targetId,
            edges: allEdges,
            nodes,
            globalSettings,
          }),
        }
      : {}),
    qos: configurationQos({
      nodeId: targetId,
      nodeCategory: targetNodeCategory,
      edges: allEdges,
      nodes,
    }),
    snmp: {
      config: {
        engineID: '80001f88800ce2333918e10b5e',
        read_community: 'public',
        v1: false,
        v2c: false,
        v3: false,
        write_community: 'private',
      },
      users: [],
      version: 1,
    },
    staticmc: {
      entries: [],
      version: 1,
    },
    stp: configurationSTP({
      nodeCategory: targetNodeCategory,
      nodes,
      edges,
    }),
    system: {
      cfgName: 'no_vlans',
      dnsclient: {
        dns_over_tcp: false,
        dns_query_retries: 2,
        dns_query_timeout: 3,
        domain_name: '',
        enabled: false,
        server_primary: '0.0.0.0',
        server_secondary: '0.0.0.0',
      },
      files: {
        webexport: !!isAutoDeploy,
      },
      info: {
        contact: 'contact',
        description: 'description',
        location: 'location',
      },
      version: 2,
    },
    trap: {
      trap: {
        community: 'trap',
        destination: '192.168.1.1',
        enabled: false,
        version: 'v1',
      },
      version: 2,
    },
    usbapi: {
      state: true,
      version: 1,
    },
    user: {
      users: ensureDefaultUsers(globalSettings?.userCredentials).map(
        (user) => ({
          level: user.level,
          name: user.name,
          password: user.passwordHash,
          salt: user.salt,
        }),
      ),
      version: 1,
    },
    vlan: configurationVlan({
      nodeId: targetId,
      nodeCategory: targetNodeCategory,
      edges: allEdges,
      nodes,
      vlans,
      isAutoDeploy,
      autoDeployIps,
    }),
  };

  // change jsonData system.cfgName to cfgName
  jsonData.system.cfgName = cfgName;

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('jsonData', jsonData);
  }

  const compactData = JSON.stringify(jsonData, null, 0);
  const contentChecksum = crc32.str(compactData);

  const compressedData = pako.gzip(compactData, { level: 9 });
  const compressedBase64 = btoa(
    String.fromCharCode(...new Uint8Array(compressedData)),
  );

  const roqscfg = {
    'roqs-container': {
      'content-list': [
        {
          'content-checksum': contentChecksum >>> 0,
          'content-compatibility': [],
          'content-data': compressedBase64,
          'content-encoding': 4,
          'content-id': `${targetNode.key}`,
          'content-name': `${cfgName}.cfg`,
          'content-scope': 1,
          'content-type': 1,
          'content-version': 1,
        },
      ],
      'data-checksum': 0,
      'meta-version': 1,
    },
  };

  roqscfg['roqs-container']['data-checksum'] =
    crc32.str(JSON.stringify(roqscfg, null, 0)) >>> 0;
  // download the configuration .cfg file
  // Use pretty-print format to match ROQSTAR export (4 spaces indentation, \n line endings)
  const prettyJson = JSON.stringify(roqscfg, null, 4);
  // Ensure Unix line endings (\n) to match ROQSTAR format
  const normalizedJson = prettyJson.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const file = new Blob([normalizedJson], {
    type: 'application/json',
  });
  return new File([file], `${cfgName}.cfg`, { type: 'application/json' });
};
